import { initializeApp, type FirebaseApp } from 'firebase/app';
import {
  browserLocalPersistence,
  browserPopupRedirectResolver,
  browserSessionPersistence,
  indexedDBLocalPersistence,
  initializeAuth,
  type Auth,
} from 'firebase/auth';
import { getFirestore, type Firestore } from 'firebase/firestore';
import { getStorage, type FirebaseStorage } from 'firebase/storage';
import { getFunctions, type Functions } from 'firebase/functions';
import { initializeAppCheck, ReCaptchaV3Provider, type AppCheck } from 'firebase/app-check';

const e = import.meta.env;
const currentHost = typeof window === 'undefined' ? '' : window.location.hostname.toLowerCase();
const sameSiteAuthHosts = new Set(['mattermaze.fun', 'reactive-worlds.web.app']);
const config = {
  apiKey: e.VITE_FIREBASE_API_KEY as string | undefined,
  authDomain: sameSiteAuthHosts.has(currentHost)
    ? currentHost
    : e.VITE_FIREBASE_AUTH_DOMAIN as string | undefined,
  projectId: e.VITE_FIREBASE_PROJECT_ID as string | undefined,
  storageBucket: e.VITE_FIREBASE_STORAGE_BUCKET as string | undefined,
  messagingSenderId: e.VITE_FIREBASE_MESSAGING_SENDER_ID as string | undefined,
  appId: e.VITE_FIREBASE_APP_ID as string | undefined,
  measurementId: e.VITE_FIREBASE_MEASUREMENT_ID as string | undefined,
};
export let firebaseMode = false;
export let firebaseInitializationError: Error | null = null;
export let firebaseApp: FirebaseApp | null = null;
export let auth: Auth | null = null;
export let db: Firestore | null = null;
export let storage: FirebaseStorage | null = null;
export let cloudFunctions: Functions | null = null;
export let appCheck: AppCheck | null = null;
if (config.apiKey && config.authDomain && config.projectId && config.storageBucket && config.appId) {
  try {
    const app = initializeApp(config);
    firebaseApp = app;
    const appCheckSiteKey = e.VITE_FIREBASE_APP_CHECK_SITE_KEY as string | undefined;
    if (appCheckSiteKey) {
      const debugToken = e.VITE_FIREBASE_APP_CHECK_DEBUG_TOKEN as string | undefined;
      if (e.DEV && debugToken)
        (globalThis as typeof globalThis & { FIREBASE_APPCHECK_DEBUG_TOKEN?: boolean | string }).FIREBASE_APPCHECK_DEBUG_TOKEN = debugToken === 'true' ? true : debugToken;
      appCheck = initializeAppCheck(app, {
        provider: new ReCaptchaV3Provider(appCheckSiteKey),
        isTokenAutoRefreshEnabled: true,
      });
    }
    auth = initializeAuth(app, {
      persistence: [indexedDBLocalPersistence, browserLocalPersistence, browserSessionPersistence],
      popupRedirectResolver: browserPopupRedirectResolver,
    });
    db = getFirestore(app); storage = getStorage(app); cloudFunctions = getFunctions(app, 'europe-west1');
    firebaseMode = true;
  } catch (error) {
    firebaseInitializationError = error instanceof Error ? error : new Error('Firebase initialization failed.');
    console.error(firebaseInitializationError);
  }
}
